import React, { useContext } from 'react';
import { DownloadOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import BillingBillingTable from '@totem/components/ticketing/billing/billedSubTable';
import BillingContext from '@totem/components/ticketing/billing/billingContext';
import { BilledEntry } from '@totem/components/ticketing/ticketing';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import Hours from '@totem/components/ui_components/widgets/hours/hours';
import { formatDateOnly, isNotNull } from '@totem/utilities/common';
import { downloadFile } from '@totem/utilities/fileUtilities';
import { sortStringAscending } from '@totem/utilities/tableUtilities';

const escapeCsvValue = (value: string | number) => {
  const str = String(value ?? '');
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const BilledTable = () => {
  const { loading, billedData } = useContext(BillingContext);

  const handleExport = () => {
    const headers = [
      'Organization',
      'Sales Order',
      'Created By',
      'Created Date',
      'Date',
      'Hours',
      'Resource',
      'Description',
      'Task',
      'Ticket Number',
      'Summary',
      'Building',
    ];

    const rows = (billedData ?? []).flatMap((entry) =>
      entry.items.map((item) => [
        escapeCsvValue(entry.organization.name),
        escapeCsvValue(entry.billingId),
        escapeCsvValue(entry.createdBy),
        escapeCsvValue(formatDateOnly(entry.createdAt)),
        escapeCsvValue(formatDateOnly(item.timeEntry.date)),
        escapeCsvValue(item.timeEntry.hoursLogged),
        escapeCsvValue(item.resource.name),
        escapeCsvValue(item.timeEntry.description),
        escapeCsvValue(
          item.task?.name ? `${item.task.number}: ${item.task.name}` : '',
        ),
        escapeCsvValue(item.ticket.number),
        escapeCsvValue(item.ticket.summary),
        escapeCsvValue(item.building.name),
      ]),
    );

    const csv = [headers.join(','), ...rows.map((row) => row.join(','))].join(
      '\n',
    );
    downloadFile('billed', csv);
  };

  const columns: ColumnProps<BilledEntry>[] = [
    {
      title: 'Organization',
      dataIndex: 'organization.name',
      key: 'organization.id',
      render: (_, record: BilledEntry) => record.organization.name,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.organization.name, compB.organization.name),
    },
    {
      title: 'Sales Order',
      dataIndex: 'billingId',
      key: 'billingId',
      render: (_, record: BilledEntry) => record.billingId,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.billingId, compB.billingId),
    },
    {
      title: 'Hours',
      align: 'right',
      dataIndex: '',
      key: '',
      render: (_, record: BilledEntry) => {
        let billedHours = 0;
        record.items.forEach(
          (item) => (billedHours += item.timeEntry.hoursLogged),
        );
        return (
          <b>
            <Hours amount={billedHours} />
          </b>
        );
      },
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      render: (_, record: BilledEntry) => record.createdBy,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.createdBy, compB.createdBy),
    },
    {
      title: 'Created Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (_, record: BilledEntry) => formatDateOnly(record.createdAt),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.createdAt, compB.createdAt),
    },
  ];

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: 8,
        }}
      >
        <Button icon={<DownloadOutlined />} onClick={handleExport}>
          Export CSV
        </Button>
      </div>
      <IBTable
        showSorterTooltip
        columns={columns}
        dataSource={isNotNull(billedData) ? billedData : []}
        loading={loading}
        pagination={{
          showSizeChanger: true,
        }}
        rowKey={(record) => record.billingId}
        expandable={{
          expandedRowRender: (record) => (
            <BillingBillingTable billedEntry={record} />
          ),
          defaultExpandAllRows: false,
        }}
      />
    </>
  );
};

export default BilledTable;
